import { apiClient, API_CONFIG } from '../config/api'
import { Balise, BaliseFilters, PaginatedResult, BaliseStatistics } from '../types/balise'

const BM = API_CONFIG.BM_BASE_URL

function normalizeDate(value: any): string | undefined {
  if (!value) return undefined
  if (typeof value === 'string') return value
  if (Array.isArray(value)) {
    const [y, mo = 1, d = 1, h = 0, mi = 0, s = 0] = value
    return new Date(y, mo - 1, d, h, mi, s).toISOString()
  }
  return undefined
}

function normalizeBalise(raw: any): Balise {
  return {
    id: raw.id,
    universalId: raw.universal_id ?? raw.universalId,
    brand: raw.brand,
    brandDeviceId: raw.brand_device_id ?? raw.brandDeviceId ?? null,
    lockCode: raw.lock_code ?? raw.lockCode,
    deviceName: raw.device_name ?? raw.deviceName,
    deviceType: raw.device_type ?? raw.deviceType ?? null,
    status: raw.status,
    customerId: raw.customer_id ?? raw.customerId,
    customerName: raw.customer_name ?? raw.customerName,
    assignmentStatus: raw.assignment_status ?? raw.assignmentStatus,
    batteryLevel: raw.battery_level ?? raw.batteryLevel,
    latitude: raw.latitude,
    longitude: raw.longitude,
    lastSeen: normalizeDate(raw.last_seen ?? raw.lastSeen),
    createdAt: normalizeDate(raw.created_at ?? raw.createdAt),
    updatedAt: normalizeDate(raw.updated_at ?? raw.updatedAt),
    containerId: raw.container_id ?? raw.containerId,
    assetId: raw.asset_id ?? raw.assetId,
    // A balise is assigned if the status says so OR any target FK is set —
    // checking container_id alone made asset-mounted balises show
    // "Unassigned" and "Assigned" at the same time in different widgets.
    isAssigned: raw.assignment_status === 'ASSIGNED' || raw.container_id != null || (raw.asset_id ?? raw.assetId) != null,
  }
}

export const baliseService = {
  async getAllBalises(page = 1, size = 20, filters: BaliseFilters = {}): Promise<PaginatedResult<Balise>> {
    const params: Record<string, string | number> = { page, size }
    if (filters.search) params.search = filters.search
    if (filters.assignmentStatus) params.assignmentStatus = filters.assignmentStatus
    if (filters.operationalStatus) params.operationalStatus = filters.operationalStatus
    if (filters.customerId) params.customerId = filters.customerId
    if (filters.brand) params.brand = filters.brand
    if (filters.excludeBrand) params.excludeBrand = filters.excludeBrand

    const { data: res } = await apiClient.get(`${BM}/balises`, params)
    const normalized = Array.isArray(res?.data) ? res.data.map(normalizeBalise) : []
    return {
      data: normalized,
      total: res?.total ?? 0,
      page: res?.page ?? page,
      size: res?.size ?? size,
      totalPages: res?.totalPages ?? 0,
    }
  },

  async getBaliseByUniversalId(universalId: string): Promise<Balise> {
    const { data } = await apiClient.get(`${BM}/balises/${encodeURIComponent(universalId)}`)
    return normalizeBalise(data)
  },

  async getBaliseStatistics(): Promise<BaliseStatistics> {
    try {
      const { data: raw } = await apiClient.get(`${BM}/balises/statistics`)
      const total = raw?.total_balises ?? raw?.total ?? 0
      const assigned = raw?.assigned_balises ?? raw?.assigned ?? 0
      const unassigned = raw?.available_balises ?? raw?.unassigned ?? 0
      const online = raw?.active_balises ?? raw?.online ?? 0
      return { total, assigned, unassigned, online, offline: Math.max(total - online, 0), needsAttention: raw?.needs_attention ?? 0 }
    } catch {
      return { total: 0, assigned: 0, unassigned: 0, online: 0, offline: 0, needsAttention: 0 }
    }
  },

  async createBalise(baliseData: Partial<Balise>): Promise<Balise> {
    const { data } = await apiClient.post(`${BM}/balises`, baliseData)
    return data
  },

  async updateBalise(universalId: string, baliseData: Partial<Balise>): Promise<Balise> {
    const { data } = await apiClient.put(`${BM}/balises/${encodeURIComponent(universalId)}`, baliseData)
    return data
  },

  async deleteBalise(universalId: string): Promise<void> {
    await apiClient.delete(`${BM}/balises/${encodeURIComponent(universalId)}`)
  },

  async assignBaliseToCustomer(universalId: string, customerId: number, customerName: string): Promise<Balise> {
    const params = new URLSearchParams({ customerId: String(customerId), customerName })
    const { data } = await apiClient.post(`${BM}/balises/${encodeURIComponent(universalId)}/assign?${params}`)
    return normalizeBalise(data)
  },

  async unassignBaliseFromCustomer(lockCode: string): Promise<Balise> {
    const { data } = await apiClient.post(`${BM}/balises/${encodeURIComponent(lockCode)}/unassign`)
    return normalizeBalise(data)
  },

  async assignBaliseToContainer(lockCode: string, containerId: number, containerName: string) {
    const { data } = await apiClient.post(`${BM}/balises/${encodeURIComponent(lockCode)}/assign-container`, { containerId, containerName })
    return data
  },

  // Asset assignment shares the same balise inventory as containers but
  // hits a separate Java endpoint (BaliseResource#assignBaliseToAsset).
  // The legacy Ionic flow lumps Containers and Assets into the same
  // selector card for the operator — we mirror that in the new
  // container-assignment modal.
  async assignBaliseToAsset(lockCode: string, assetId: number) {
    const { data } = await apiClient.post(
      `${BM}/balises/${encodeURIComponent(lockCode)}/assign-asset`,
      { assetId },
    )
    return data
  },

  async unassignBalise(lockCode: string) {
    const { data } = await apiClient.post(`${BM}/balises/${encodeURIComponent(lockCode)}/unassign-customer`)
    return data
  },

  async sealLock(lockCode: string, brand?: string, options?: Record<string, any>) {
    const { data } = await apiClient.post(`${BM}/balises/commands/${encodeURIComponent(lockCode)}/seal`, { ...options, brand })
    return data as { success: boolean; message?: string; error?: string }
  },

  async unsealLock(lockCode: string, brand?: string, options?: Record<string, any>) {
    const { data } = await apiClient.post(`${BM}/balises/commands/${encodeURIComponent(lockCode)}/unseal`, { ...options, brand })
    return data as { success: boolean; message?: string; error?: string }
  },

  async changeDeviceMode(lockCode: string, brand: string, mode: number) {
    const { data } = await apiClient.post(`${BM}/balises/commands/${encodeURIComponent(lockCode)}/device-mode`, { brand, mode })
    return data as { success: boolean; message?: string; error?: string }
  },

  // Balise-to-vehicle assignment history (fleet-api, reads balise_assignment_history)
  async getAssignmentHistory(lockCode: string): Promise<AssignmentHistoryEntry[]> {
    const { data } = await apiClient.get(`/balises/${encodeURIComponent(lockCode)}/assignment-history`)
    return (data || []) as AssignmentHistoryEntry[]
  },

  async getSyncStatus(lockCode: string) {
    const { data } = await apiClient.get(`${BM}/balise-sync/${encodeURIComponent(lockCode)}/status`)
    return data
  },

  async getSyncHistory(lockCode: string) {
    const { data } = await apiClient.get(`${BM}/balise-sync/${encodeURIComponent(lockCode)}/history`)
    return data
  },

  async syncAction(lockCode: string, action: 'activate' | 'deactivate' | 'maintain') {
    const { data } = await apiClient.post(`${BM}/balise-sync/${encodeURIComponent(lockCode)}/${action}`)
    return data
  },

  async decommission(lockCode: string) {
    const { data } = await apiClient.post(`${BM}/balises/decommission/${encodeURIComponent(lockCode)}`)
    return data
  },

  /**
   * Recent device events for the per-device detail panel (P2).
   *
   * Hits the fleet-api endpoint added in server.js — `/api/balises/:lockCode/
   * recent-events`. Reads orchestration_business_events directly so it works
   * for ANY brand. Primary consumer right now is the JIMI detail tab,
   * because the legacy trajectory/timeline endpoints filter on
   * latitude IS NOT NULL — fine for MAXVISION (which always has GPS) but
   * useless for JIMI (where the bridge intentionally leaves GPS columns
   * null until the payload schema lands in P3).
   */
  async getRecentEvents(
    lockCode: string,
    opts: { hours?: number; limit?: number; sourceSystem?: string } = {}
  ): Promise<RecentEventsResult> {
    const params: Record<string, string | number> = {}
    if (opts.hours) params.hours = opts.hours
    if (opts.limit) params.limit = opts.limit
    if (opts.sourceSystem) params.sourceSystem = opts.sourceSystem
    const { data } = await apiClient.get(
      `/balises/${encodeURIComponent(lockCode)}/recent-events`,
      params
    )
    return data as RecentEventsResult
  },

  // P7: aggregated JIMI pipeline health (orchestration freshness +
  // jimi-receiver MQTT state + jimi-bridge cycle/watermark). The browser
  // never holds the receiver/bridge tokens — fleet-api proxies the
  // upstream calls server-side. The endpoint always responds 200; per-
  // component failures are encoded inside the body so the UI can render
  // partial truth.
  async getJimiHealth(): Promise<JimiHealth> {
    const { data } = await apiClient.get('/jimi/health')
    return data as JimiHealth
  },

  // JIMI vendor catalog (TurboHive devices). Used by the JIMI tab in
  // BaliseManagement to surface devices that exist on the vendor side
  // but have not yet been onboarded to orchestration_db. The endpoint
  // always responds 200; if the upstream is unreachable, `ok` is false
  // and `captors` is an empty array.
  async getJimiCaptors(): Promise<JimiCaptorListResponse> {
    const { data } = await apiClient.get('/jimi/captors')
    return data as JimiCaptorListResponse
  },
}

export interface AssignmentHistoryEntry {
  id: number
  lock_code: string
  universal_id?: string
  brand?: string
  device_name?: string
  assignment_type: 'ASSET' | 'CONTAINER' | 'CUSTOMER'
  assignment_date: string
  unassignment_date: string | null
  assigned_by?: string
  unassigned_by?: string
  asset_id?: number
  asset_code?: string
  license_plate?: string
  container_id?: number
  container_name?: string
  customer_name?: string
  is_active: boolean
}

export interface RecentEventEntry {
  id: number
  eventTimestamp: string
  eventType: string
  sourceSystem: string
  sourceEventId: string
  eventDescription?: string
  eventSeverity?: string
  hasGps: boolean
  payloadSize: number
}

export interface RecentEventsResult {
  lockCode: string
  hours: number
  sourceSystem: string | null
  summary: {
    totalEvents: number
    gpsEvents: number
    firstEventAt: string | null
    lastEventAt: string | null
    byEventType: Record<string, number>
  }
  events: RecentEventEntry[]
}

// P7: JIMI pipeline health, mirrors the fleet-api response shape exactly.
// Per-component "status" follows a 4-level ladder: OK (green), DEGRADED
// or STALE (amber), DOWN (red), UNREACHABLE (gray). `overall` is the
// worst of the three components.
export type JimiComponentStatus = 'OK' | 'DEGRADED' | 'STALE' | 'DOWN' | 'UNREACHABLE'

export interface JimiOrchestrationHealth {
  status: JimiComponentStatus
  lastJimiEventAt: string | null
  secondsSinceLastEvent: number | null
  totalJimiEvents24h: number
  jimiDevicesActive24h: number
  error: string | null
}

export interface JimiReceiverHealth {
  status: JimiComponentStatus
  mqttConnected: boolean
  subscribedTopicCount: number
  totals: Record<string, number> | null
  error: string | null
}

export interface JimiBridgeHealth {
  status: JimiComponentStatus
  lastCycleAt: string | null
  watermarkLagSeconds: number | null
  staleAfterSeconds: number | null
  cycleCount: number
  cumulative: { fetched: number; written: number; skipped: number; duplicate: number; failed: number } | null
  lastCycle: { fetched: number; written: number; skipped: number; duplicate: number; failed: number; watermarkAfter?: string | null } | null
  error: string | null
}

export interface JimiHealth {
  overall: JimiComponentStatus
  checkedAt: string
  orchestration: JimiOrchestrationHealth
  receiver: JimiReceiverHealth
  bridge: JimiBridgeHealth
}

// JIMI vendor catalog entry. fleet-api normalizes the TurboHive shape
// before returning, so the UI never has to map raw vendor field names.
// `_raw` keeps the original record for debug / drill-down tooltips.
export interface JimiCaptor {
  id: string | number | null
  imei: string | null
  name: string | null
  model: string | null
  status: string | null
  lastSeen: string | null
  _raw: Record<string, unknown>
}

export interface JimiCaptorListResponse {
  ok: boolean
  checkedAt: string
  captors: JimiCaptor[]
  total: number
  error?: string
  upstreamMessage?: string | null
}
